import { Link, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, Clock as ClockIcon, ChevronLeft } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useAuth } from '@/lib/auth-store';
import { useAssessmentTreeQuery, useAssessmentByItemQuery } from '@/hooks/queries/useAssessment';
import type { SubmitResultShape } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Post-submit / post-grade view. Two entry shapes:
 *   1. Coming from AssessmentTaker.submit() → result is in location.state.
 *   2. Direct nav to /result/:attemptId — Phase-3 next-slice fetches the attempt.
 *      For now we show a "fetch your past attempts" stub when state is empty.
 */
export function AttemptResult() {
  const { state: auth } = useAuth();
  const { orgSlug = '', courseSlug = '', itemId = '' } = useParams();
  const location = useLocation();
  const fromState = (location.state as { result?: SubmitResultShape } | null)?.result ?? null;

  const byItem = useAssessmentByItemQuery(
    auth.status === 'authenticated' ? auth.token : '',
    itemId,
  );
  const tree = useAssessmentTreeQuery(
    auth.status === 'authenticated' ? auth.token : '',
    byItem.data?.assessment.id ?? null,
  );

  if (auth.status !== 'authenticated') return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />
      <main className="container max-w-3xl py-8">
        <Link
          to={`/c/${orgSlug}/${courseSlug}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden /> Back to course
        </Link>

        {fromState ? (
          <ResultBody result={fromState} questions={tree.data?.questions ?? []} />
        ) : (
          <div className="mt-8 rounded-2xl border border-border bg-card p-8 text-center">
            <ClockIcon className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
            <h1 className="mt-3 font-display text-xl font-semibold">Result not loaded.</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Direct-link result viewing ships in Phase 3 next slice. For now, results show right
              after you submit. Reach out if you can&apos;t see your score.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function ResultBody({
  result,
  questions,
}: {
  result: SubmitResultShape;
  questions: ReadonlyArray<{ id: string; promptHtml: string; points: number }>;
}) {
  const percent = result.maxScore > 0 ? Math.round((result.autoScore / result.maxScore) * 100) : 0;

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-4 rounded-3xl bg-gradient-to-br from-primary via-primary to-[hsl(220,80%,18%)] px-8 py-10 text-primary-foreground shadow-xl"
      >
        <div className="flex items-center gap-3">
          {result.requiresInstructorReview ? (
            <AlertTriangle className="h-6 w-6" aria-hidden />
          ) : (
            <CheckCircle2 className="h-6 w-6" aria-hidden />
          )}
          <h1 className="font-display text-2xl font-bold tracking-tight">
            {result.requiresInstructorReview
              ? 'Submitted — instructor review pending'
              : 'Submitted!'}
          </h1>
        </div>
        <p className="mt-3 text-3xl font-bold">
          {result.autoScore} / {result.maxScore}
          <span className="ml-2 text-base font-normal text-primary-foreground/80">{percent}%</span>
        </p>
        <p className="mt-1 text-sm text-primary-foreground/80">
          {result.requiresInstructorReview
            ? 'Auto-graded portion shown above. Open-ended responses are with your instructor.'
            : 'Auto-graded by EduBorg. Per-question feedback below.'}
        </p>
      </motion.section>

      <section className="mt-8 space-y-3">
        <h2 className="font-display text-lg font-semibold">Per-question feedback</h2>
        <ol className="space-y-2">
          {result.perQuestion.map(({ questionId, result: r }, ix) => {
            const q = questions.find((qq) => qq.id === questionId);
            const ok = r.awarded === r.max;
            const partial = r.awarded !== null && r.awarded > 0 && r.awarded < r.max;
            return (
              <li
                key={questionId}
                className={cn(
                  'rounded-2xl border bg-card p-4',
                  r.requiresInstructor
                    ? 'border-border'
                    : ok
                      ? 'border-accent/30'
                      : partial
                        ? 'border-gold/30'
                        : 'border-destructive/30',
                )}
              >
                <header className="flex items-start justify-between gap-3">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Q{ix + 1}
                  </span>
                  <span className="text-sm font-semibold">
                    {r.awarded === null ? '—' : r.awarded} / {r.max}
                  </span>
                </header>
                {q?.promptHtml && (
                  <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">
                    {q.promptHtml}
                  </p>
                )}
                <p className="mt-2 text-sm">{r.reason}</p>
              </li>
            );
          })}
        </ol>
      </section>
    </>
  );
}
